<script setup lang="ts">
import type { Schemas } from '@shopware/api-client/api-types/storeApiTypes';

const props = defineProps<{
    block: Schemas['CmsBlock'];
}>();

const { getSlotContent } = useCmsBlock(props.block);
const { getProducts } = useGetProduct();

const config = getSlotContent('config') as Schemas['CmsSlot'];
const image = getSlotContent('image') as Schemas['CmsSlot'];

const elementConfig = useCmsElementConfig(config);
const imagePosition = elementConfig.getConfigValue('imagePosition');
const productIds = elementConfig.getConfigValue('products');
const { data: products } = await getProducts(productIds);
</script>

<template>
    <div class="mt-16 grid gap-4 md:mt-100 md:grid-cols-2 md:items-center md:gap-8">
        <CmsLoader
            :content="config"
            :class="{
                'order-1': imagePosition === 'left',
                'order-1 md:order-[unset]': imagePosition === 'right',
            }"
        />

        <CmsElementImage
            :element="image"
            :shadow-position="imagePosition"
            :small-image="true"
            class="!min-h-[unset]"
        />
    </div>

    <template v-if="products?.elements?.length > 1">
        <LayoutSlider
            classes="w-screen -mx-6 mt-4 md:mx-[unset] md:mt-6 md:w-[unset]"
            :class="{
                'cursor-grab': products.elements.length > 1,
            }"
            :left-arrow-class="{
                'hidden md:flex !p-0': true,
                '!right-10 !-top-7 !left-[unset]': imagePosition === 'left',
                '!left-0 !-top-7': imagePosition === 'right',
            }"
            :right-arrow-class="{
                'hidden md:flex !p-0': true,
                '!right-0 !-top-7': imagePosition === 'left',
                '!left-10 !-top-7': imagePosition === 'right',
            }"
            :space-between="32"
            :slides-per-view="1.4"
            :breakpoints="{
                540: {
                    slidesPerView: 2,
                },
                768: {
                    slidesPerView: 3,
                },
                1024: {
                    slidesPerView: 4,
                },
            }"
        >
            <LayoutSliderSlide
                v-for="product in products.elements"
                :key="product.id"
            >
                <ProductCard
                    :product="product"
                    displayMode="contain"
                    layout="minimal"
                    class="max-w-[unset]"
                />
            </LayoutSliderSlide>
        </LayoutSlider>
    </template>
</template>

<style scoped>
swiper-container > swiper-slide:last-child {
    @apply pr-6 md:pr-[unset];
}

swiper-container > swiper-slide:first-child {
    @apply pl-6 md:pl-[unset];
}
</style>
